import { useState } from "react";
import { Disc3, ExternalLink, Link2, Newspaper, Star, Trophy } from "lucide-react";
import { useT } from "@/i18n";
import type { ProfileArticle, ProfileSection } from "@/lib/profile.functions";

export const SECTION_KINDS = [
  { value: "about", labelKey: "profiles.ambKindAbout" },
  { value: "career", labelKey: "profiles.ambKindCareer" },
  { value: "discography", labelKey: "profiles.ambKindDiscography" },
  { value: "gallery", labelKey: "profiles.ambKindGallery" },
  { value: "link", labelKey: "profiles.ambKindLink" },
] as const;

function byKind(sections: ProfileSection[], kind: string) {
  return sections.filter((s) => s.kind === kind);
}

/** Rich content blocks shown on Official Platform Ambassador pages. */
export function AmbassadorContent({
  sections,
  articles,
}: {
  sections: ProfileSection[];
  articles: ProfileArticle[];
}) {
  const { t } = useT();
  const about = byKind(sections, "about");
  const career = byKind(sections, "career");
  const disco = byKind(sections, "discography");
  const gallery = byKind(sections, "gallery");
  const links = byKind(sections, "link");

  if (!sections.length && !articles.length) return null;

  return (
    <>
      {about.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold">
            <Star className="size-5 text-primary" /> {t("profiles.ambKindAbout")}
          </h2>
          <div className="mt-3 space-y-5">
            {about.map((s) => (
              <article key={s.id} className="space-y-2">
                <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                {s.subtitle && <p className="text-sm text-muted-foreground">{s.subtitle}</p>}
                {s.image_url && (
                  <img
                    src={s.image_url}
                    alt={s.title}
                    loading="lazy"
                    className="w-full rounded-xl object-cover"
                  />
                )}
                {s.body && <p className="whitespace-pre-line text-muted-foreground">{s.body}</p>}
                {s.link_url && <SectionLink url={s.link_url} label={t("profiles.ambOpenLink")} />}
              </article>
            ))}
          </div>
        </section>
      )}

      {career.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold">
            <Trophy className="size-5 text-primary" /> {t("profiles.ambKindCareer")}
          </h2>
          <ol className="mt-4 space-y-4 border-l border-border pl-5">
            {career.map((s) => (
              <li key={s.id} className="relative">
                <span className="absolute -left-[26px] top-1.5 size-3 rounded-full bg-primary" />
                <p className="font-display font-semibold">
                  {s.year ? <span className="text-primary">{s.year} · </span> : null}
                  {s.title}
                </p>
                {s.subtitle && <p className="text-sm text-muted-foreground">{s.subtitle}</p>}
                {s.body && (
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{s.body}</p>
                )}
                {s.link_url && <SectionLink url={s.link_url} label={t("profiles.ambOpenLink")} />}
              </li>
            ))}
          </ol>
        </section>
      )}

      {disco.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold">
            <Disc3 className="size-5 text-primary" /> {t("profiles.ambKindDiscography")}
          </h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            {disco.map((s) => (
              <div
                key={s.id}
                className="overflow-hidden rounded-xl border border-border transition hover:border-primary"
              >
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt={s.title}
                    loading="lazy"
                    className="aspect-square w-full object-cover"
                  />
                ) : (
                  <div className="flex aspect-square items-center justify-center bg-muted">
                    <Disc3 className="size-10 text-muted-foreground" />
                  </div>
                )}
                <div className="space-y-1 p-3">
                  <p className="font-display font-semibold leading-tight">{s.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {[s.subtitle, s.year].filter(Boolean).join(" · ")}
                  </p>
                  {s.body && <p className="text-xs text-muted-foreground">{s.body}</p>}
                  {s.link_url && <SectionLink url={s.link_url} label={t("profiles.ambListen")} />}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {gallery.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="font-display text-xl font-bold">{t("profiles.ambKindGallery")}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {gallery.map((s) =>
              s.image_url ? (
                <figure key={s.id} className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={s.image_url}
                    alt={s.title}
                    loading="lazy"
                    className="h-48 w-full object-cover"
                  />
                  <figcaption className="p-2 text-xs text-muted-foreground">{s.title}</figcaption>
                </figure>
              ) : null,
            )}
          </div>
        </section>
      )}

      {links.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold">
            <Link2 className="size-5 text-primary" /> {t("profiles.ambKindLink")}
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {links.map((s) => (
              <a
                key={s.id}
                href={s.link_url ?? "#"}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-between gap-3 rounded-xl border border-border p-4 transition hover:border-primary"
              >
                <span>
                  <span className="block font-display font-semibold">{s.title}</span>
                  {s.subtitle && (
                    <span className="block text-xs text-muted-foreground">{s.subtitle}</span>
                  )}
                </span>
                <ExternalLink className="size-4 text-primary" />
              </a>
            ))}
          </div>
        </section>
      )}

      {articles.length > 0 && (
        <section className="card-surface mt-4 p-6">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold">
            <Newspaper className="size-5 text-primary" /> {t("profiles.ambArticles")}
          </h2>
          <div className="mt-3 space-y-4">
            {articles.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function ArticleCard({ article }: { article: ProfileArticle }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <article className="overflow-hidden rounded-xl border border-border">
      {article.cover_url && (
        <img
          src={article.cover_url}
          alt={article.title}
          loading="lazy"
          className="h-56 w-full object-cover"
        />
      )}
      <div className="space-y-2 p-4">
        <h3 className="font-display text-lg font-bold">{article.title}</h3>
        <p className="text-xs text-muted-foreground">
          {new Date(article.published_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </p>
        {article.excerpt && <p className="text-sm text-muted-foreground">{article.excerpt}</p>}
        {open && article.body && (
          <p className="whitespace-pre-line text-sm text-muted-foreground">{article.body}</p>
        )}
        <div className="flex flex-wrap items-center gap-4 pt-1">
          {article.body && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-sm font-medium text-primary underline-offset-2 hover:underline"
            >
              {open ? t("profiles.ambShowLess") : t("profiles.ambReadMore")}
            </button>
          )}
          {article.link_url && (
            <SectionLink url={article.link_url} label={t("profiles.ambOpenLink")} />
          )}
        </div>
      </div>
    </article>
  );
}

function SectionLink({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
    >
      {label} <ExternalLink className="size-3.5" />
    </a>
  );
}
